import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { RefreshCw, Mail, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (v) => v != null ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v) : "$0.00";
const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "\u2014";

function InvoiceActions({ inv, onSync, onResend }) {
  return (
    <div className="flex gap-1">
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onSync(inv.id)} title="Sync" data-testid={`invoices-sync-${inv.id}`}>
        <RefreshCw className="w-3 h-3" />
      </Button>
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onResend(inv.id)} title="Resend" data-testid={`invoices-resend-${inv.id}`}>
        <Mail className="w-3 h-3" />
      </Button>
      {inv.hosted_invoice_url && (
        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
          <Button variant="ghost" size="icon" className="h-7 w-7" title="Open in Stripe" data-testid={`invoices-external-${inv.id}`}>
            <ExternalLink className="w-3 h-3" />
          </Button>
        </a>
      )}
    </div>
  );
}

function InvoiceRow({ inv, onSync, onResend }) {
  return (
    <TableRow key={inv.id} className="table-row-hover" data-testid={`invoice-row-${inv.id}`}>
      <TableCell className="font-medium text-sm">{inv.dealer_name || "\u2014"}</TableCell>
      <TableCell className="font-mono text-xs">{inv.stripe_invoice_number}</TableCell>
      <TableCell className="text-right tabular-nums">{formatCurrency(inv.invoice_amount)}</TableCell>
      <TableCell className="text-sm">{formatDate(inv.issue_date)}</TableCell>
      <TableCell className="text-sm">{formatDate(inv.due_date)}</TableCell>
      <TableCell className="text-sm">{formatDate(inv.paid_at)}</TableCell>
      <TableCell>
        <div className="flex gap-1">
          <StatusBadge status={inv.status} />
          {inv.overdue_flag && inv.status !== "paid" && <StatusBadge status="overdue" />}
        </div>
      </TableCell>
      <TableCell><StatusBadge status={inv.payment_method_expected} /></TableCell>
      <TableCell><InvoiceActions inv={inv} onSync={onSync} onResend={onResend} /></TableCell>
    </TableRow>
  );
}

export default function Invoices() {
  const { token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [syncing, setSyncing] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 100 };
      if (filter === "overdue") params.overdue_only = true;
      if (filter === "open" || filter === "paid") params.status = filter;
      const res = await axios.get(`${API}/invoices`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(res.data.invoices || []);
    } catch (err) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleResend = async (id) => {
    try {
      await axios.post(`${API}/invoices/${id}/resend`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Invoice resent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend");
    }
  };

  const handleSync = async (id) => {
    try {
      await axios.post(`${API}/invoices/${id}/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Invoice synced");
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync");
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/sync/invoices`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Synced ${res.data.synced} invoices`);
      fetchInvoices();
    } catch (err) {
      toast.error("Failed to sync all");
    } finally {
      setSyncing(false);
    }
  };

  const overdueCount = invoices.filter(inv => inv.overdue_flag && inv.status !== "paid").length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="invoices-title">Invoices</h1>
        <p className="text-sm text-muted-foreground mt-1">Track all billing invoices and payment status</p>
      </div>

      {/* Overdue banner */}
      {filter !== "overdue" && overdueCount > 0 && (
        <div className="flex items-center gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50" data-testid="overdue-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">{overdueCount} overdue invoice{overdueCount > 1 ? "s" : ""} need attention</p>
            <p className="text-xs text-amber-700 mt-0.5">These invoices are past their Net 15 due date and unpaid.</p>
          </div>
          <Button variant="outline" size="sm" className="border-amber-300 text-amber-900 hover:bg-amber-100" onClick={() => setFilter("overdue")} data-testid="view-overdue">
            View Overdue
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
            <div className="flex gap-2">
              {[{key: "", label: "All"}, {key: "open", label: "Open"}, {key: "paid", label: "Paid"}, {key: "overdue", label: "Overdue"}].map((f) => (
                <Button
                  key={f.key}
                  variant={filter === f.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter(f.key)}
                  data-testid={`invoices-filter-${f.key || 'all'}`}
                  className={f.key === "overdue" && filter !== "overdue" && overdueCount > 0 ? "border-amber-300 text-amber-900" : ""}
                >
                  {f.label}
                  {f.key === "overdue" && overdueCount > 0 && (
                    <span className="ml-1.5 bg-amber-200 text-amber-900 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">{overdueCount}</span>
                  )}
                </Button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={handleSyncAll} disabled={syncing} data-testid="sync-all-invoices">
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? "Syncing..." : "Sync All from Stripe"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dealer</TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Issued</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Paid Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1,2,3,4,5].map((key) => (
                  <TableRow key={`skeleton-${key}`}><TableCell colSpan={9}><div className="h-8 bg-muted rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : invoices.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  {filter === "overdue" ? "No overdue invoices" : "No invoices found"}
                </TableCell></TableRow>
              ) : (
                invoices.map((inv) => (
                  <InvoiceRow key={inv.id} inv={inv} onSync={handleSync} onResend={handleResend} />
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
